import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Icon } from "@/lib/ui";
import { Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

export default function AdminThemes() {
  const [themes, setThemes] = useState(null);
  const [cats, setCats] = useState([]);
  const [edit, setEdit] = useState(null);

  const load = useCallback(async () => { const { data } = await api.get("/admin/themes"); setThemes(data.data); }, []);
  useEffect(() => { load(); api.get("/admin/categories").then((r) => setCats(r.data.data)); }, [load]);

  const save = async (form) => {
    try {
      if (form.id) await api.put(`/admin/themes/${form.id}`, form);
      else await api.post("/admin/themes", form);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { try { await api.delete(`/admin/themes/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-black text-3xl tracking-tight">Themes</h1>
        <button data-testid="add-theme-btn" onClick={() => setEdit({ name: "", category_id: cats[0]?.id, accent: "#007AFF", surface: "#EAF3FF", is_default: false, status: "active", description: "" })} className="tap px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold text-sm flex items-center gap-2"><Icon name="Plus" className="w-4 h-4" /> New</button>
      </div>

      {themes === null ? <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div> :
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {themes.map((t) => (
            <div key={t.id} className="rounded-2xl border border-border bg-background overflow-hidden" data-testid={`theme-card-${t.name}`}>
              <div className="h-20" style={{ backgroundColor: t.accent }} />
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <p className="font-display font-bold">{t.name}</p>
                  {t.is_default && <span className="text-xs font-bold text-status-active">Default</span>}
                </div>
                <p className="text-xs text-muted-foreground">{t.category_name || "All categories"}</p>
                <div className="flex gap-2 mt-3">
                  <button data-testid={`edit-theme-${t.name}`} onClick={() => setEdit(t)} className="tap flex-1 py-1.5 rounded-lg border border-border font-semibold text-sm">Edit</button>
                  <button onClick={() => del(t.id)} className="tap px-2.5 py-1.5 rounded-lg border border-border text-status-lost"><Icon name="Trash2" className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>}

      <ThemeDialog data={edit} cats={cats} onClose={() => setEdit(null)} onSave={save} />
    </div>
  );
}

function ThemeDialog({ data, cats, onClose, onSave }) {
  const [form, setForm] = useState(data || {});
  useEffect(() => setForm(data || {}), [data]);
  if (!data) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-w-md">
        <DialogHeader><DialogTitle className="font-display text-2xl">{form.id ? "Edit" : "New"} theme</DialogTitle></DialogHeader>
        <input data-testid="theme-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="Theme name" className="w-full h-12 px-4 rounded-xl border border-input focus:outline-none focus:ring-2 focus:ring-ring" />
        <div>
          <span className="text-sm font-semibold">Category</span>
          <Select value={form.category_id || ""} onValueChange={(v) => set("category_id", v)}>
            <SelectTrigger className="mt-1.5 h-12 rounded-xl" data-testid="theme-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm font-semibold">Accent<input type="color" value={form.accent || "#007AFF"} onChange={(e) => set("accent", e.target.value)} className="mt-1 w-full h-11 rounded-lg border border-input" /></label>
          <label className="text-sm font-semibold">Surface<input type="color" value={form.surface || "#EAF3FF"} onChange={(e) => set("surface", e.target.value)} className="mt-1 w-full h-11 rounded-lg border border-input" /></label>
        </div>
        <div className="flex items-center justify-between rounded-xl border border-border p-3.5">
          <span className="font-semibold text-sm">Set as category default</span>
          <Switch checked={!!form.is_default} onCheckedChange={(v) => set("is_default", v)} data-testid="theme-default" />
        </div>
        <button data-testid="theme-save" onClick={() => onSave(form)} className="tap w-full h-12 rounded-full bg-primary text-primary-foreground font-bold">Save</button>
      </DialogContent>
    </Dialog>
  );
}
